import axios, { AxiosInstance, AxiosResponse } from 'axios';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  meta?: {
    pagination?: any;
    [key: string]: any;
  };
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

interface Site {
  id: string;
  domain: string;
  name: string;
  api_key: string;
  default_language: string;
  enabled_languages: string[];
  total_keys: number;
  translated_percentage: number;
  created_at: string;
}

interface Translation {
  id: string;
  content_key: string;
  original_text: string;
  translations: Record<string, string>;
  quality_scores?: Record<string, number>; // Language code -> quality score (0-1)
  status: string;
  updated_at: string;
}

interface Language {
  code: string;
  name: string;
  native_name: string;
  rtl: boolean;
}

interface LoginRequest {
  email: string;
  password: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AuthResponse {
  token: string;
  user: User;
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.removeToken();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Token management
  setToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  removeToken(): void {
    localStorage.removeItem('auth_token');
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // Authentication endpoints
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.api.post<ApiResponse<AuthResponse>>('/auth/login', credentials);
    if (response.data.success && response.data.data) {
      this.setToken(response.data.data.token);
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Login failed');
  }

  async getProfile(): Promise<User> {
    const response = await this.api.get<ApiResponse<User>>('/auth/profile');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to get profile');
  }

  logout(): void {
    this.removeToken();
    window.location.href = '/login';
  }

  // Generic HTTP methods for auth endpoints
  async post<T = any>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.api.post<T>(url, data);
  }

  async get<T = any>(url: string, config?: { params?: Record<string, any> }): Promise<AxiosResponse<T>> {
    return this.api.get<T>(url, config);
  }

  async put<T = any>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.api.put<T>(url, data);
  }

  // Sites endpoints
  async getSites(page: number = 1, limit: number = 20): Promise<{ sites: Site[], pagination: any }> {
    const response = await this.api.get<ApiResponse>(`/sites?page=${page}&limit=${limit}`);
    if (response.data.success) {
      return {
        sites: response.data.data,
        pagination: response.data.meta?.pagination
      };
    }
    throw new Error(response.data.error?.message || 'Failed to get sites');
  }

  async createSite(siteData: { domain: string; name: string; default_language?: string }): Promise<Site> {
    const response = await this.api.post<ApiResponse<Site>>('/sites', siteData);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to create site');
  }

  async getSiteDetails(siteId: string): Promise<Site> {
    const response = await this.api.get<ApiResponse<Site>>(`/sites/${siteId}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to get site details');
  }

  async enableLanguagesForSite(siteId: string, languageCodes: string[]): Promise<{ enabled_languages: string[], newly_enabled: string[] }> {
    const response = await this.api.post<ApiResponse>(`/sites/${siteId}/languages`, {
      language_codes: languageCodes
    });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to enable languages');
  }

  // Translations endpoints
  async getSiteTranslations(
    siteId: string,
    options: {
      language?: string;
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
    } = {}
  ): Promise<{ translations: Translation[], pagination: any }> {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value.toString());
      }
    });

    const response = await this.api.get<ApiResponse>(`/translations/sites/${siteId}?${params}`);
    if (response.data.success && response.data.data) {
      return {
        translations: response.data.data.translations,
        pagination: response.data.data.pagination
      };
    }
    throw new Error(response.data.error?.message || 'Failed to get translations');
  }

  async updateTranslation(
    translationId: string,
    updates: { translated_text: string; status?: string }
  ): Promise<Translation> {
    const response = await this.api.put<ApiResponse<Translation>>(`/translations/${translationId}`, updates);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to update translation');
  }

  // Languages endpoints
  async getLanguages(): Promise<Language[]> {
    const response = await this.api.get<ApiResponse<Language[]>>('/languages');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to get languages');
  }

  // Generic error handler
  private handleError(error: any): never {
    if (error.response?.data?.error?.message) {
      throw new Error(error.response.data.error.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('An unexpected error occurred');
    }
  }

  // ===== TRANSLATION MEMORY APIs =====

  async addToTranslationMemory(data: {
    siteId: string;
    sourceLanguage: string;
    targetLanguage: string;
    sourceText: string;
    targetText: string;
    qualityScore?: number;
    context?: string;
    approvedBy?: string;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/translation-memory', data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to add to translation memory');
  }

  async getTranslationMemorySuggestions(data: {
    siteId: string;
    sourceText: string;
    sourceLanguage: string;
    targetLanguage: string;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/translation-memory/suggestions', data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to get suggestions');
  }

  async getFuzzyMatches(data: {
    siteId: string;
    sourceText: string;
    sourceLanguage: string;
    targetLanguage: string;
    threshold?: number;
    limit?: number;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/translation-memory/fuzzy-matches', data);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to get fuzzy matches');
  }

  async recordTranslationMemoryUsage(data: {
    siteId: string;
    memoryId: string;
    translationId: string;
    similarity: number;
    accepted: boolean;
  }): Promise<void> {
    await this.api.post<ApiResponse>('/translation-memory/usage', data);
  }

  async getTranslationMemoryStatistics(siteId: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/translation-memory/statistics?siteId=${siteId}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to get statistics');
  }

  async getTopTranslations(siteId: string, limit: number = 10): Promise<any[]> {
    const response = await this.api.get<ApiResponse>(`/translation-memory/top-translations?siteId=${siteId}&limit=${limit}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to get top translations');
  }

  async exportTranslationMemory(siteId: string, sourceLanguage?: string, targetLanguage?: string): Promise<any[]> {
    const params = new URLSearchParams({ siteId });
    if (sourceLanguage) params.append('sourceLanguage', sourceLanguage);
    if (targetLanguage) params.append('targetLanguage', targetLanguage);

    const response = await this.api.get<ApiResponse>(`/translation-memory/export?${params}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to export translation memory');
  }

  async deleteTranslationMemoryEntry(id: string): Promise<void> {
    await this.api.delete(`/translation-memory/${id}`);
  }

  // ===== GLOSSARY APIs =====

  async createGlossary(data: {
    siteId: string;
    name: string;
    description?: string;
    domain?: string;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>('/glossaries', data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to create glossary');
  }

  async getGlossaries(siteId: string): Promise<any[]> {
    const response = await this.api.get<ApiResponse>(`/glossaries?siteId=${siteId}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to get glossaries');
  }

  async getGlossaryById(glossaryId: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/glossaries/${glossaryId}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to get glossary');
  }

  async updateGlossary(glossaryId: string, data: {
    name?: string;
    description?: string;
    domain?: string;
    isActive?: boolean;
  }): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/glossaries/${glossaryId}`, data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to update glossary');
  }

  async deleteGlossary(glossaryId: string): Promise<void> {
    await this.api.delete(`/glossaries/${glossaryId}`);
  }

  async addGlossaryTerm(glossaryId: string, data: {
    sourceTerm: string;
    sourceLanguage: string;
    translations: Record<string, string>;
    doNotTranslate?: boolean;
    caseSensitive?: boolean;
    notes?: string;
    examples?: string[];
  }): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/glossaries/${glossaryId}/terms`, data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to add term');
  }

  async updateGlossaryTerm(termId: string, data: {
    translations?: Record<string, string>;
    doNotTranslate?: boolean;
    caseSensitive?: boolean;
    notes?: string;
    examples?: string[];
  }): Promise<any> {
    const response = await this.api.put<ApiResponse>(`/glossaries/terms/${termId}`, data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to update term');
  }

  async deleteGlossaryTerm(termId: string): Promise<void> {
    await this.api.delete(`/glossaries/terms/${termId}`);
  }

  async findTermsInText(glossaryId: string, text: string): Promise<any[]> {
    const response = await this.api.post<ApiResponse>(`/glossaries/${glossaryId}/find-terms`, { text });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to find terms');
  }

  async applyGlossary(glossaryId: string, text: string, targetLanguage: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/glossaries/${glossaryId}/apply`, { text, targetLanguage });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to apply glossary');
  }

  async importGlossaryFromCSV(glossaryId: string, csvContent: string): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/glossaries/${glossaryId}/import-csv`, { csvContent });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to import CSV');
  }

  async exportGlossaryToCSV(glossaryId: string): Promise<string> {
    const response = await this.api.get(`/glossaries/${glossaryId}/export-csv`, {
      responseType: 'text'
    });
    return response.data;
  }

  async getGlossaryStatistics(glossaryId: string): Promise<any> {
    const response = await this.api.get<ApiResponse>(`/glossaries/${glossaryId}/statistics`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to get glossary statistics');
  }

  async searchGlossaryTerms(glossaryId: string, query: string): Promise<any[]> {
    const response = await this.api.get<ApiResponse>(`/glossaries/${glossaryId}/search?q=${encodeURIComponent(query)}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to search terms');
  }

  async bulkAddGlossaryTerms(glossaryId: string, terms: any[]): Promise<any> {
    const response = await this.api.post<ApiResponse>(`/glossaries/${glossaryId}/bulk-add-terms`, { terms });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error?.message || 'Failed to bulk add terms');
  }
}

export default new ApiService();
export type { Site, Translation, Language, User, AuthResponse };